import { apiService } from './api';

class BackgroundEmailService {
  private intervalRef: NodeJS.Timeout | null = null;
  private isRunning = false;
  private fetchInterval = 5000; // 5 seconds
  private consecutiveErrors = 0;
  private maxConsecutiveErrors = 5; // Stop after 5 consecutive errors

  constructor() {
    // Auto-start when service is created
    this.start();
  }

  start(): void {
    if (this.isRunning || this.intervalRef) {
      return; // Already running
    }

    console.log('🔄 Starting background email service (5-second intervals)');
    this.isRunning = true;

    // Initial fetch
    this.fetchEmails();

    // Set up interval for continuous fetching
    this.intervalRef = setInterval(() => {
      this.fetchEmails();
    }, this.fetchInterval);
  }

  stop(): void {
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
      this.intervalRef = null;
    }
    this.isRunning = false;
    console.log('⏹️ Background email service stopped');
  }

  restart(): void {
    console.log('🔄 Restarting background email service');
    this.stop();
    this.consecutiveErrors = 0; // Reset error counter
    this.start();
  }

  private async fetchEmails(): Promise<void> {
    try {
      console.log('📧 [Background] Fetching emails...');
      const result = await apiService.manualFetchEmails();
      
      if (result.success) {
        const emailsFound = result.debug?.emailsFound || 0;
        const processed = result.debug?.processedCount || 0;
        
        if (emailsFound > 0) {
          console.log(`✅ [Background] Found ${emailsFound} emails, processed ${processed}`);
        } else {
          console.log('📧 [Background] No new emails found');
        }
        
        // Reset error counter on success
        this.consecutiveErrors = 0;
      } else {
        console.warn('⚠️ [Background] Email fetch failed:', result.message);
        this.consecutiveErrors++;
      }
    } catch (error) {
      console.error('❌ [Background] Email fetch error:', error);
      this.consecutiveErrors++;
      
      // Check if we should stop due to too many consecutive errors
      if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
        console.error(`🛑 [Background] Stopping email service after ${this.consecutiveErrors} consecutive errors`);
        this.stop();
      }
      
      // Special handling for authentication errors
      if (error instanceof Error && error.message.includes('Authentication failed')) {
        console.warn('🔒 [Background] Authentication issue detected - will retry but won\'t logout');
      }
    }
  }

  // Getters for status
  get isActive(): boolean {
    return this.isRunning;
  }

  get interval(): number {
    return this.fetchInterval;
  }

  // Allow changing interval if needed
  setInterval(milliseconds: number): void {
    this.fetchInterval = milliseconds;
    if (this.isRunning) {
      this.stop();
      this.start();
    }
  }
}

// Create singleton instance
export const backgroundEmailService = new BackgroundEmailService();

export default backgroundEmailService;